import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getPatientById,
  getPatientAssessment,
  getAssessmentNotes,
} from "../../../services/patientService";
import PrintShell from "../../../components/UI/PrintShell";
import AssessmentPrintSheet from "./AssessmentPrintSheet";
import { loadPatientUiConfig } from "../usePatientTabs";
import { mergeAssessmentPayload, CLIENT_ASSESSMENT_TITLE } from "../assessmentForm.shared";
import {
  PATIENT_NOTE_TYPE_ASSESSMENT,
  extractPatientNotes,
} from "../patientNote.shared";

function extractPatient(patientRes) {
  const payload = patientRes?.result || patientRes?.data || patientRes;
  return payload?.patient || payload || null;
}

/**
 * Opens in its own tab from the Assessment tab (same pattern as Demographics print).
 */
const PatientAssessmentPrintPage = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const chartId = searchParams.get("chartId");

  const [patient, setPatient] = useState(null);
  const [assessmentPayload, setAssessmentPayload] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setError("");

    (async () => {
      try {
        const uiConfig = await loadPatientUiConfig();
        if (cancelled) return;
        if (!uiConfig.enabledTabKeys.includes("assessment")) {
          setError("Assessment is not enabled for this agency");
          return;
        }

        if (!chartId) {
          setError("Chart context is required to print assessment");
          return;
        }

        const [patientRes, assessmentRes] = await Promise.all([
          getPatientById(patientId),
          getPatientAssessment(patientId, { chartId: Number(chartId) }),
        ]);

        if (cancelled) return;

        const nextPatient = extractPatient(patientRes);
        if (!nextPatient?.id && !nextPatient?.mrn) {
          throw new Error("Patient record was empty");
        }
        setPatient(nextPatient);

        const raw =
          assessmentRes?.result?.assessment?.payload ||
          assessmentRes?.assessment?.payload;
        setAssessmentPayload(mergeAssessmentPayload(raw));

        let nextNotes = [];
        try {
          const notesRes = await getAssessmentNotes(patientId, {
            page: 1,
            limit: 200,
            type: PATIENT_NOTE_TYPE_ASSESSMENT,
          });
          if (!cancelled) {
            nextNotes = extractPatientNotes(notesRes);
          }
        } catch (notesErr) {
          console.warn("Assessment print: notes failed to load", notesErr);
          nextNotes = [];
        }
        if (cancelled) return;
        setNotes(nextNotes);
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load assessment");
          setReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId, chartId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
        <div className="max-w-sm rounded-xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-800">
            Assessment could not be loaded
          </p>
          <p className="mt-1 text-xs text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen animate-pulse bg-slate-100 py-10">
        <div className="mx-auto h-[700px] w-full max-w-4xl rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  const patientName =
    [patient.firstName, patient.middleName, patient.lastName]
      .filter(Boolean)
      .join(" ") || patient.patientName;

  return (
    <PrintShell
      title={CLIENT_ASSESSMENT_TITLE}
      subtitle={`${patientName || "Client"} · MRN ${patient.mrn || "—"}`}
      ready={ready}
      contentClassName="print:py-0"
    >
      <AssessmentPrintSheet
        patient={patient}
        payload={assessmentPayload}
        notes={notes}
      />
    </PrintShell>
  );
};

export default PatientAssessmentPrintPage;
